import React, { FC, useContext, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import {
    createWebhookSubscriptionTemplate,
    deleteWebhookSubscriptionTemplate,
    getWebhookSubscriptionsForOrganizationTemplate,
    rotateWebhookSubscriptionSecretTemplate,
    setWebhookSubscriptionActiveTemplate,
    triggerWebhookSubscriptionTestTemplate,
} from 'apollo/templates/webhook-subscription';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import Loader from 'components/shared/loader/loader.component';
import {
    CreateWebhookSubscriptionInput,
    WebhookSubscription,
    WebhookSubscriptionWithSecret,
} from 'models/webhook-subscription';
import CreateWebhookSubscriptionDialog from './create-webhook-subscription-dialog.component';
import WebhookSubscriptionSecretDialog from './webhook-subscription-secret-dialog.component';
import { OrganizationRef } from './types';

type PendingAction =
    | { type: 'rotate'; subscription: WebhookSubscription }
    | { type: 'delete'; subscription: WebhookSubscription };

type WebhookSubscriptionsProps = {
    organization: OrganizationRef | null;
};

const WebhookSubscriptions: FC<WebhookSubscriptionsProps> = ({ organization }: WebhookSubscriptionsProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [revealedSecret, setRevealedSecret] = useState<WebhookSubscriptionWithSecret | null>(null);

    const [fetchSubscriptions, { data, loading }] = useLazyQuery<{
        webhookSubscriptionsForOrganization: WebhookSubscription[];
    }>(getWebhookSubscriptionsForOrganizationTemplate, {
        fetchPolicy: 'network-only',
        onError: showBoundary,
    });
    const subscriptions = data?.webhookSubscriptionsForOrganization ?? [];

    useEffect(() => {
        if (organization?._id) {
            fetchSubscriptions({ variables: { organizationId: organization._id } });
        }
    }, [organization, fetchSubscriptions]);

    const refetch = () => {
        if (organization?._id) {
            fetchSubscriptions({ variables: { organizationId: organization._id } });
        }
    };

    const showToast = (message: string) => {
        setAppToasterContent({ isOpen: true, severity: 'success', message });
    };

    const [createSubscription] = useMutation<{ createWebhookSubscription: WebhookSubscriptionWithSecret }>(
        createWebhookSubscriptionTemplate,
        {
            onCompleted: ({ createWebhookSubscription: result }) => {
                setRevealedSecret(result);
                refetch();
                showToast(translate('app-settings:WEBHOOK_CREATED'));
            },
            onError: (error) => {
                if (error.graphQLErrors?.[0]?.message === 'INVALID_URL') {
                    setAppToasterContent({
                        isOpen: true,
                        severity: 'error',
                        message: translate('app-settings:WEBHOOK_URL_REJECTED'),
                    });
                    return;
                }
                showBoundary(error);
            },
        },
    );

    const [rotateSecret] = useMutation<{ rotateWebhookSubscriptionSecret: WebhookSubscriptionWithSecret }>(
        rotateWebhookSubscriptionSecretTemplate,
        {
            onCompleted: ({ rotateWebhookSubscriptionSecret: result }) => {
                setRevealedSecret(result);
                refetch();
                showToast(translate('app-settings:WEBHOOK_ROTATED'));
            },
            onError: showBoundary,
        },
    );

    const [setActive] = useMutation(setWebhookSubscriptionActiveTemplate, {
        onCompleted: () => {
            refetch();
            showToast(translate('app-settings:WEBHOOK_UPDATED'));
        },
        onError: showBoundary,
    });

    const [deleteSubscription] = useMutation(deleteWebhookSubscriptionTemplate, {
        onCompleted: () => {
            refetch();
            showToast(translate('app-settings:WEBHOOK_DELETED'));
        },
        onError: showBoundary,
    });

    const [triggerTest] = useMutation(triggerWebhookSubscriptionTestTemplate, {
        onCompleted: () => {
            showToast(translate('app-settings:WEBHOOK_TEST_QUEUED'));
        },
        onError: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('app-settings:WEBHOOK_TEST_FAILED'),
            });
        },
    });

    const handleCreate = (input: CreateWebhookSubscriptionInput) => {
        createSubscription({ variables: { input } });
    };

    const handleConfirmPending = () => {
        if (!pendingAction) return;
        if (pendingAction.type === 'rotate') {
            rotateSecret({ variables: { id: pendingAction.subscription._id } });
        } else if (pendingAction.type === 'delete') {
            deleteSubscription({ variables: { id: pendingAction.subscription._id } });
        }
        setPendingAction(null);
    };

    const handleToggleActive = (subscription: WebhookSubscription) => {
        setActive({ variables: { id: subscription._id, active: !subscription.active } });
    };

    const formatDateOrDash = (iso?: string) => (iso ? formatDate(new Date(iso), 'datetime') : '—');

    return (
        <Box>
            <Typography variant="h2" gutterBottom>
                {translate('app-settings:WEBHOOKS')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('app-settings:WEBHOOKS_HELP')}
            </Typography>

            <Box display="flex" gap={2} alignItems="center" mb={3}>
                <Button variant="contained" onClick={() => setIsCreateOpen(true)} disabled={!organization}>
                    {translate('app-settings:CREATE_WEBHOOK')}
                </Button>
            </Box>

            {organization && loading && <Loader />}

            {organization && !loading && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('app-settings:WEBHOOK_NAME')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_URL')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_ENTITIES')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_LAST_SUCCESS')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_LAST_FAILURE')}</TableCell>
                                <TableCell>{translate('app-settings:WEBHOOK_FAILURES')}</TableCell>
                                <TableCell align="center">{translate('app-settings:WEBHOOK_ACTIVE')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subscriptions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {translate('app-settings:WEBHOOKS_EMPTY')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {subscriptions.map((subscription) => (
                                <TableRow key={subscription._id}>
                                    <TableCell>
                                        <Typography variant="body2">{subscription.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontFamily="monospace"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {subscription.url}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            {subscription.entities.map((entity) => (
                                                <Chip
                                                    key={entity}
                                                    size="small"
                                                    label={translate(`app-settings:WEBHOOK_ENTITY_${entity}`)}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatDateOrDash(subscription.lastSuccessAt)}</TableCell>
                                    <TableCell>
                                        {subscription.lastError ? (
                                            <Tooltip title={subscription.lastError}>
                                                <span>{formatDateOrDash(subscription.lastFailureAt)}</span>
                                            </Tooltip>
                                        ) : (
                                            formatDateOrDash(subscription.lastFailureAt)
                                        )}
                                    </TableCell>
                                    <TableCell>{subscription.failureCount}</TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            checked={subscription.active}
                                            onChange={() => handleToggleActive(subscription)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip
                                            title={translate(
                                                subscription.active
                                                    ? 'app-settings:WEBHOOK_TEST_HELP'
                                                    : 'app-settings:WEBHOOK_TEST_INACTIVE',
                                            )}
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!subscription.active}
                                                    onClick={() => triggerTest({ variables: { id: subscription._id } })}
                                                >
                                                    <SendIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={translate('app-settings:WEBHOOK_ROTATE_SECRET')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setPendingAction({ type: 'rotate', subscription })}
                                            >
                                                <AutorenewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={translate('common:DELETE')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setPendingAction({ type: 'delete', subscription })}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {organization && (
                <CreateWebhookSubscriptionDialog
                    open={isCreateOpen}
                    organizationId={organization._id}
                    organizationName={organization.name}
                    onCancel={() => setIsCreateOpen(false)}
                    onSubmit={(input) => {
                        setIsCreateOpen(false);
                        handleCreate(input);
                    }}
                />
            )}

            <ConfirmDialog
                open={pendingAction !== null}
                handleClose={() => setPendingAction(null)}
                handleConfirm={handleConfirmPending}
                dialogTitle={
                    pendingAction?.type === 'rotate'
                        ? translate('app-settings:WEBHOOK_ROTATE_SECRET_CONFIRM_TITLE')
                        : translate('app-settings:DELETE_WEBHOOK_CONFIRM_TITLE')
                }
                dialogContent={
                    pendingAction?.type === 'rotate'
                        ? translate('app-settings:WEBHOOK_ROTATE_SECRET_CONFIRM_TEXT', {
                              name: pendingAction.subscription.name,
                          })
                        : pendingAction
                          ? translate('app-settings:DELETE_WEBHOOK_CONFIRM_TEXT', {
                                name: pendingAction.subscription.name,
                            })
                          : undefined
                }
            />

            {revealedSecret && (
                <WebhookSubscriptionSecretDialog
                    open
                    url={revealedSecret.subscription.url}
                    secret={revealedSecret.secret}
                    onClose={() => setRevealedSecret(null)}
                />
            )}
        </Box>
    );
};

export default WebhookSubscriptions;
